import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ILookupItem } from '../../types/genericTypes';
import { createTeam, createTeamConfig, deleteTeam, getDatasourceList, getTeamConfigAndTeamDetails, getTeamConfigByTeamId, getTeamDetails, getTeamList, getTeamTypeList, updateTeam, updateTeamConfig } from '../API/teamApi';
import type { AppDispatch, RootState } from '../store/store';
import { createTeamConfigPayload, transformTeamConfigForDispaly, transformTeamForDisplay } from '../transform/teamTransform';
import { transformForLookupList } from '../transform/transformLookup';
import { selectLoggedInUserId } from './authSlice';


export interface ITeam {
    name: string,
    displayName: string,
    fullyQualifiedName: string,
    description: string,
    isJoinable: boolean,
    deleted: boolean,
    id: string,
    teamType: string,
    updatedAt: number,
    updatedBy: string,
    users: any[],
    tenantId?: string,
    tenantName: string,
    tierType: string
}

export const initialTeamForm: ITeam = {
    name: '',
    displayName: '',
    fullyQualifiedName: '',
    description: '',
    isJoinable: false,
    deleted: false,
    id: '',
    teamType: '',
    updatedAt: 0,
    updatedBy: '',
    users: [],
    tenantId: '',
    tenantName: '',
    tierType: ''
}

export interface ITeamConfig {
  id?: string,
  teamId: string,
  adminId: string,
  name: string,
  fullyQualifiedName: string,
  displayName: string,
  description: string,
  version?: number,
  updatedAt?: number,
  updatedBy?: string,
  href?: string,
  owner?: {
    id: string,
    type: string,
    name: string,
    fullyQualifiedName: string,
    description: string,
    displayName: string,
    deleted: boolean,
    href: string
  },
  deleted: boolean,
  teamRoles: string[],
  dataSources: string[],
  rateLimits: any[],
}

export const initialTeamConfig: ITeamConfig = {
  teamId: '',
  adminId: '',
  name: '',
  fullyQualifiedName: '',
  displayName: '',
  description: '',
  href: '',
  deleted: false,
  teamRoles: [],
  dataSources: [],
  rateLimits: [],
}

export interface IDatasource {
    id: string;
    description: string;
    name: string;
    dataSourceType: string;
    groupKey: string;
    pipelineKey: string;
    regions: string[];
    active: boolean;
    flag: boolean;
    tier: string;
    weight: number;
    taxonomies: string[];
    fullyQualifiedName: string;
    href: string;
    updatedAt: number;
    updatedBy: string;
    createdAt: number;
    version: number;
    deleted: boolean;
}

interface TeamState {
    teamList: ITeam[],
    teamLookup: ILookupItem[],
    teamTypeList: string[],
    datasourceList: IDatasource[],
    teamDetails: ITeam,
    teamConfiguration: ITeamConfig
}

// Define the initial state using that type
const initialState: TeamState = {
    teamList: [],
    teamLookup: [],
    teamTypeList: [],
    datasourceList: [],
    teamDetails: initialTeamForm,
    teamConfiguration: initialTeamConfig
};

export const callGetTeamList = createAsyncThunk(
    'teams/getTeamList',
    async (args, { dispatch }) => {
        const response = await getTeamList();
        dispatch(setTeamList(response.data.data));
        dispatch(setTeamLookup(transformForLookupList(response.data.data, 'displayName')))
    }
);

export const callGetTeamTypeList = createAsyncThunk(
  'teams/getTeamTypes',
  async (args, { dispatch }) => {
    const response = await getTeamTypeList();
    dispatch(setTeamTypeList(response.data.data));
  }
);

export const callGetDatasourceList = createAsyncThunk<IDatasource[]>(
  'teams/getDatsourceList',
  async (args, { dispatch }) => {
    const response = await getDatasourceList();
    dispatch(setDatasourceList(response.data));
    return response.data;
  }
)

export const callCreateTeam = createAsyncThunk<any, { team: ITeam, config: ITeamConfig }, { dispatch: AppDispatch, state: RootState }>(
    'teams/createTeam',
    async ({ team, config }, { dispatch, getState }) => {
      const state = getState();
      try {
        const teamPayload = { ...team, fullyQualifiedName: team.name };
        const response = await createTeam(teamPayload);
        if (response.data) {
          const adminId = selectLoggedInUserId(state);
          const configPayload = createTeamConfigPayload(response.data, config, adminId);
          const response2 = await createTeamConfig(configPayload);
          
        } else {
          throw('There was an error creating the team.')
        }
        
        return response.data.id;
      } catch (error) {

      }
    }
);

export const callUpdateTeam = createAsyncThunk<any, { team: ITeam, config: ITeamConfig }, { dispatch: AppDispatch, state: RootState }>(
    'teams/updateTeam',
    async ({ team, config }, { dispatch, getState }) => {
        const state = getState();
        try {
          const response = await updateTeam(team);
          if (response.data) {
            const adminId = selectLoggedInUserId(state);
            const configPayload = createTeamConfigPayload(response.data, config, adminId);
            const response2 = await updateTeamConfig(configPayload);
          } else {
            throw ('There was an error updating this team.')
          }
          return response.data.id;
        } catch (error) {

      }
    }
)

export const callDeleteTeam = createAsyncThunk(
  'teams/deleteTeam',
  async (teamId: string, { dispatch }) => {
      await deleteTeam(teamId);
      dispatch(callGetTeamList());
  }
)

export const callGetTeamDetails = createAsyncThunk(
    'teams/getTeamDetails',
    async (teamId: string, { dispatch }) => {
        // TODO;
        const configAndTeamDetails = await getTeamConfigAndTeamDetails(teamId);
        const config = configAndTeamDetails.data.teamConfiguration;
        const team = configAndTeamDetails.data.team;
        const transformedTeam = transformTeamForDisplay(team);
        dispatch(setTeamDetails(transformedTeam));
        const transformedConfig = transformTeamConfigForDispaly(config);
        dispatch(setTeamConfig(transformedConfig));
    }
);

export const teamSlice = createSlice({
  name: 'team',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setTeamList: (state, action: PayloadAction<ITeam[]>) => {
        state.teamList = action.payload;
    },
    setTeamTypeList: (state, action: PayloadAction<string[]>) => {
      state.teamTypeList = action.payload;
    },
    setDatasourceList: (state, action: PayloadAction<IDatasource[]>) => {
      state.datasourceList = action.payload
        .filter(datasource => datasource.deleted !== true);
    },
    setTeamLookup: (state, action: PayloadAction<ILookupItem[]>) => {
        state.teamLookup = action.payload;
    },
    setTeamDetails: (state, action: PayloadAction<ITeam>) => {
        state.teamDetails = action.payload;
    },
    setTeamConfig: (state, action: PayloadAction<ITeamConfig>) => {
      state.teamConfiguration = action.payload;
    }
  }
});

export const {
  setTeamList,
  setTeamTypeList,
  setDatasourceList,
  setTeamLookup,
  setTeamDetails,
  setTeamConfig
} = teamSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectTeamList = (state: RootState): ITeam[] => state.team.teamList;
export const selectTeamLookup = (state: RootState): ILookupItem[] => state.team.teamLookup;
export const selectTeamTypeList = (state: RootState): string[] => state.team.teamTypeList;
export const selectDatasourceList = (state: RootState): IDatasource[] => state.team.datasourceList;
export const selectTeamDetails = (state: RootState): ITeam => state.team.teamDetails;
export const selectTeamConfig = (state: RootState): ITeamConfig => state.team.teamConfiguration;

export default teamSlice.reducer;
